export const framePaddingUnit = 0.05;
export const frameBottomUnit = 0.13;

const minAspect = 0.42;
const maxAspect = 1.8;
const visibilityTarget = 0.64;
const sampleColumns = 7;
const sampleRows = 7;
const maxCardWidth = 300;

export const createRandom = (seed) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const clamp = (value, min, max) => (min > max ? (min + max) / 2 : Math.min(max, Math.max(min, value)));

const unitCard = (aspect) => {
  const safeAspect = clamp(aspect || 0.75, minAspect, maxAspect);
  const photoWidth = Math.sqrt(safeAspect);
  const photoHeight = 1 / photoWidth;
  return {
    photoWidth,
    photoHeight,
    width: photoWidth + framePaddingUnit * 2,
    height: photoHeight + framePaddingUnit + frameBottomUnit,
  };
};

const gridCandidates = (count, area, meanWidth, meanHeight) => {
  const grids = [];
  for (let columns = 1; columns <= count; columns += 1) {
    const rows = Math.ceil(count / columns);
    if (rows * columns - count >= columns) continue;
    const cellWidth = area.width / columns;
    const cellHeight = area.height / rows;
    const fitScale = Math.min(cellWidth / meanWidth, cellHeight / meanHeight);
    grids.push({ columns, rows, cellWidth, cellHeight, fitScale });
  }
  return grids.sort((first, second) => second.fitScale - first.fitScale).slice(0, 3);
};

const drawTraits = (count, seed) => {
  const random = createRandom(seed * 7919 + count * 31);
  const rotationRange = count === 1 ? 6 : count <= 4 ? 14 : 18;
  const stackOrder = Array.from({ length: count }, (_, index) => index);
  for (let index = stackOrder.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [stackOrder[index], stackOrder[swapIndex]] = [stackOrder[swapIndex], stackOrder[index]];
  }
  return Array.from({ length: count }, (_, index) => ({
    jitterX: random() - 0.5,
    jitterY: random() - 0.5,
    sizeWobble: count <= 2 ? 1 : 0.94 + random() * 0.1,
    rotation: (random() - 0.5) * rotationRange,
    stack: stackOrder[index],
    hasTape: random() < 0.42,
    tapeRotation: (random() - 0.5) * 14,
    drift: random(),
    spin: random() - 0.5,
    delayJitter: random(),
  }));
};

const placeCards = ({ units, traits, area, grid, scale, jitterStrength, rowStagger }) => {
  const count = units.length;
  return units.map((unit, index) => {
    const trait = traits[index];
    const row = Math.floor(index / grid.columns);
    const column = index % grid.columns;
    const itemsInRow = row === grid.rows - 1 ? count - row * grid.columns : grid.columns;
    const rowInset =
      ((grid.columns - itemsInRow) * grid.cellWidth) / 2 + (row % 2 ? 1 : -1) * rowStagger * grid.cellWidth;
    const width = unit.width * scale;
    const height = unit.height * scale;
    const radians = (trait.rotation * Math.PI) / 180;
    const cosine = Math.cos(radians);
    const sine = Math.sin(radians);
    const drawnWidth = width * trait.sizeWobble;
    const drawnHeight = height * trait.sizeWobble;
    const extentX = (Math.abs(cosine) * drawnWidth + Math.abs(sine) * drawnHeight) / 2 + 3;
    const extentY = (Math.abs(sine) * drawnWidth + Math.abs(cosine) * drawnHeight) / 2 + 3;
    const centerX = clamp(
      area.left + rowInset + (column + 0.5) * grid.cellWidth + trait.jitterX * grid.cellWidth * jitterStrength,
      area.left + extentX,
      area.left + area.width - extentX
    );
    const centerY = clamp(
      area.top + (row + 0.5) * grid.cellHeight + trait.jitterY * grid.cellHeight * jitterStrength,
      area.top + extentY,
      area.top + area.height - extentY
    );
    return {
      unit,
      width,
      height,
      drawnWidth,
      drawnHeight,
      centerX,
      centerY,
      cosine,
      sine,
      scale,
    };
  });
};

const isPointCovered = (pointX, pointY, card) => {
  const offsetX = pointX - card.centerX;
  const offsetY = pointY - card.centerY;
  const localX = offsetX * card.cosine + offsetY * card.sine;
  const localY = -offsetX * card.sine + offsetY * card.cosine;
  return Math.abs(localX) <= card.drawnWidth / 2 && Math.abs(localY) <= card.drawnHeight / 2;
};

const photoSamples = (card) => {
  const ratio = card.drawnWidth / card.width;
  const photoWidth = card.unit.photoWidth * card.scale * ratio;
  const photoHeight = card.unit.photoHeight * card.scale * ratio;
  const photoTop = -card.drawnHeight / 2 + framePaddingUnit * card.scale * ratio;
  const samples = [];
  for (let row = 0; row < sampleRows; row += 1) {
    for (let column = 0; column < sampleColumns; column += 1) {
      const localX = ((column + 0.5) / sampleColumns - 0.5) * photoWidth;
      const localY = photoTop + ((row + 0.5) / sampleRows) * photoHeight;
      samples.push({
        x: card.centerX + localX * card.cosine - localY * card.sine,
        y: card.centerY + localX * card.sine + localY * card.cosine,
      });
    }
  }
  return samples;
};

const measureVisibility = (placed, order, samplesByCard) => {
  const stackLevel = new Array(placed.length);
  order.forEach((cardIndex, level) => {
    stackLevel[cardIndex] = level;
  });
  return samplesByCard.map((samples, index) => {
    let visible = 0;
    samples.forEach((sample) => {
      const isHidden = placed.some(
        (other, otherIndex) => stackLevel[otherIndex] > stackLevel[index] && isPointCovered(sample.x, sample.y, other)
      );
      if (!isHidden) visible += 1;
    });
    return visible / samples.length;
  });
};

const improveStacking = (placed, initialOrder) => {
  const samplesByCard = placed.map(photoSamples);
  let order = [...initialOrder];
  let visibility = measureVisibility(placed, order, samplesByCard);
  let bestOrder = order;
  let bestMinimum = Math.min(...visibility);
  const attempts = placed.length * 2;
  for (let attempt = 0; attempt < attempts && bestMinimum < visibilityTarget; attempt += 1) {
    const worstIndex = visibility.indexOf(Math.min(...visibility));
    const position = order.indexOf(worstIndex);
    const lift = Math.max(1, Math.ceil((order.length - position) / 2));
    const nextOrder = [...order];
    nextOrder.splice(position, 1);
    nextOrder.splice(Math.min(order.length - 1, position + lift), 0, worstIndex);
    order = nextOrder;
    visibility = measureVisibility(placed, order, samplesByCard);
    const minimum = Math.min(...visibility);
    if (minimum > bestMinimum) {
      bestMinimum = minimum;
      bestOrder = order;
    }
  }
  return { order: bestOrder, minimum: bestMinimum };
};

export const computeCollageLayout = (aspects, area, seed) => {
  const count = aspects.length;
  if (!count || area.width <= 0 || area.height <= 0) return [];

  const units = aspects.map(unitCard);
  const meanWidth = units.reduce((sum, unit) => sum + unit.width, 0) / count;
  const meanHeight = units.reduce((sum, unit) => sum + unit.height, 0) / count;
  const tallest = Math.max(...units.map((unit) => unit.height));
  const widest = Math.max(...units.map((unit) => unit.width));
  const traits = drawTraits(count, seed);
  const startingOrder = traits
    .map((trait, index) => ({ index, stack: trait.stack }))
    .sort((first, second) => first.stack - second.stack)
    .map((entry) => entry.index);
  const largestScale = Math.min(
    (area.width * 0.94) / widest,
    (area.height * 0.96) / tallest,
    maxCardWidth / unitCard(0.75).width
  );
  const startingOverlap = count === 1 ? 1 : count <= 3 ? 1.08 : count <= 8 ? 1.2 : 1.3;
  const unitAreaTotal = units.reduce((sum, unit) => sum + unit.width * unit.height, 0);
  const coverageGoal = count === 1 ? 0.5 : count <= 3 ? 0.6 : count <= 8 ? 0.95 : 1.1;
  const coverageScale = Math.sqrt((area.width * area.height * coverageGoal) / unitAreaTotal);
  const baseJitter = count <= 2 ? 0.08 : 0.36;

  const searchGrid = (grid) => {
    const startingScale = Math.min(Math.max(grid.fitScale * startingOverlap, coverageScale), largestScale);
    const smallestScale = Math.min(grid.fitScale * 0.86, startingScale);
    const rowStagger = count <= 3 || grid.columns === 1 ? 0 : 0.14;
    let best = null;
    for (let step = 0; step < 22; step += 1) {
      const scale = Math.max(smallestScale, startingScale * Math.pow(0.968, step));
      const jitterStrength = baseJitter * Math.max(0.35, 1 - step * 0.05);
      const staggerStrength = rowStagger * Math.max(0.4, 1 - step * 0.04);
      const placed = placeCards({ units, traits, area, grid, scale, jitterStrength, rowStagger: staggerStrength });
      const stacking = count === 1 ? { order: startingOrder, minimum: 1 } : improveStacking(placed, startingOrder);
      const candidate = { placed, scale, ...stacking };
      if (stacking.minimum >= visibilityTarget) return candidate;
      if (!best || stacking.minimum > best.minimum + 0.02) best = candidate;
    }
    return best;
  };

  const results = gridCandidates(count, area, meanWidth, meanHeight).map(searchGrid);
  const passing = results.filter((result) => result.minimum >= visibilityTarget);
  const chosen = passing.length
    ? passing.reduce((best, result) => (result.scale > best.scale ? result : best))
    : results.reduce((best, result) => (result.minimum > best.minimum ? result : best));

  const levelByIndex = new Array(count);
  chosen.order.forEach((cardIndex, level) => {
    levelByIndex[cardIndex] = level + 1;
  });

  return chosen.placed.map((card, index) => {
    const trait = traits[index];
    return {
      width: card.width,
      height: card.height,
      photoWidth: card.unit.photoWidth * card.scale,
      photoHeight: card.unit.photoHeight * card.scale,
      framePadding: framePaddingUnit * card.scale,
      scale: trait.sizeWobble,
      centerX: card.centerX,
      centerY: card.centerY,
      rotation: trait.rotation,
      zIndex: levelByIndex[index],
      hasTape: trait.hasTape,
      tapeRotation: trait.tapeRotation,
      drift: trait.drift,
      spin: trait.spin,
      delayJitter: trait.delayJitter,
    };
  });
};
